import styled from '@emotion/styled';
import { useEnvVariables } from '@innovamat/ga-features';
import { getTypography } from '@innovamat/glimmer-components';
import { useWebviewBridge } from '@innovamat/webview-bridge';
import { useRef } from 'react';
import { useSmartGuides } from '../../context/SmartGuidesProvider';
import {
  PrintableBody1,
  PrintableBody2,
} from '../../printable-styles/printable-typography';
import type { AllMaterials, Printable } from '../../types/Materials';
import { downloadFile } from '../../utils/downloadFile';
import { slugify } from '../../utils/string';
import { PreprInnerHtml } from '../PreprInnerHtml';
import { Resource } from './Resource';

const Container = styled.div<{ hideOnPrint?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media print {
    ${({ hideOnPrint }) =>
      hideOnPrint &&
      `
      display: none;
    `}
  }
`;

const Wrapper = styled.div<{ hideOnPrint?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-secondary'].value};
  border-radius: 4px;
  height: fit-content;

  @media print {
    padding: 12px;
    gap: 8px;

    * {
      ${({ theme }) => getTypography(theme, 'Body 3')}
    }

    ${({ hideOnPrint }) =>
      hideOnPrint &&
      `
      display: none;
    `}
  }
`;

const Title = styled(PrintableBody1)`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text.text.value};

  @media print {
    font-weight: 500;
  }
`;

const NoMaterial = styled(PrintableBody2)``;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media print {
    gap: 2px;
  }
`;

export function Materials({
  applets,
  classroomMaterials,
  materialsList,
  printables,
  projectables,
  videos,
  isPrintable = false,
}: AllMaterials & { isPrintable?: boolean }): JSX.Element | null {
  const { t, setEventData, session } = useSmartGuides();

  const { isInWebview } = useWebviewBridge();
  const { APOLLO_SERVER } = useEnvVariables();

  const handleSendEvent = (event: string, printable: Printable) => {
    setEventData(event, {
      session_id: session?.id,
      resource_id: printable.id,
      printable_url: printable.url,
      printable_value: printable.value,
    });
  };

  const handleDownloadFile = (printable: Printable): void => {
    if (printable.isDisabled) return;

    handleSendEvent('lesson_subresource_download', printable);
    downloadFile(printable.url, slugify(printable.value));
  };

  const handleOpen = (printable: Printable): void => {
    const isPDF = printable.url.endsWith('.pdf');

    if (!isPDF) {
      handleDownloadFile(printable);
      return;
    }

    if (printable.isDisabled) return;

    const baseUrl = APOLLO_SERVER.endsWith('/')
      ? APOLLO_SERVER.slice(0, -1)
      : APOLLO_SERVER;
    const url = `${baseUrl}/api/preview-pdf?file=${
      printable.url
    }&name=${slugify(printable.value)}`;

    handleSendEvent('lesson_subresource_click', printable);

    window.open(url, '_blank');
  };

  const hasResources = Boolean(
    printables?.length ||
      applets?.length ||
      videos?.length ||
      projectables?.length
  );
  const hasMaterials = Boolean(
    classroomMaterials?.length || materialsList?.length
  );

  const materialText = (
    materialKey: string,
    additionalText: string | undefined,
    isOptional: boolean
  ): string => {
    return `${t(materialKey)} ${additionalText ? ` (${additionalText})` : ''} ${
      isOptional ? `(${t('digitalguides.material.optional')})` : ''
    }`;
  };

  const copyMaterialLink = (
    materialKey: string,
    materialId: number,
    event: React.MouseEvent<HTMLDivElement>
  ): void => {
    if (event?.metaKey || event?.ctrlKey) {
      const materialLink = `[[Material=${t(materialKey)}|${materialId}]]`;

      const tempInput = document.createElement('input');
      tempInput.value = materialLink;
      document.body.appendChild(tempInput);

      tempInput.select();
      document.execCommand('copy');

      document.body.removeChild(tempInput);
    }
  };

  const materiaBoxRef = useRef<HTMLDivElement>(null);

  const hasOnlyPrintables =
    printables?.length &&
    applets?.length === 0 &&
    videos?.length === 0 &&
    projectables?.length === 0;

  if (!hasResources && !hasMaterials && isPrintable) {
    return null;
  }

  return (
    <Wrapper
      ref={materiaBoxRef}
      hideOnPrint={!!hasOnlyPrintables && !hasMaterials}
    >
      {!hasResources && !hasMaterials && (
        <Container>
          <Title>{t('digitalguides.material')}</Title>
          <NoMaterial>{t('digitalguides.material.no_material')}</NoMaterial>
        </Container>
      )}

      {hasMaterials && (
        <Container>
          <Title>{t('digitalguides.material')}</Title>
          <List>
            {classroomMaterials?.map((material, index) =>
              material.split('\r\n').map((element) => (
                <Resource
                  key={element}
                  icon="ClassMaterialIcon"
                  tooltip={{
                    text: element,
                  }}
                  data-testid={`resource-material-${index}`}
                >
                  <PreprInnerHtml text={element} />
                </Resource>
              ))
            )}

            {materialsList?.map((material, index) => (
              <Resource
                onClick={(e) =>
                  copyMaterialLink(material.value, material.id, e)
                }
                id={`${material.id}`}
                icon="InnovamatLogoIcon"
                key={material.value}
                tooltip={{
                  img: material.image,
                  text: materialText(
                    material.value,
                    material.additionalText,
                    material.isOptional
                  ),
                }}
                data-testid={`resource-material-${index}`}
              >
                <PreprInnerHtml
                  text={materialText(
                    material.value,
                    material.additionalText,
                    material.isOptional
                  )}
                />
              </Resource>
            ))}
          </List>
        </Container>
      )}

      {hasResources && (
        <Container hideOnPrint={!!hasOnlyPrintables}>
          <Title>{t('digitalguides.resources')}</Title>
          <List>
            {printables?.map((printable, index) => (
              <Resource
                key={printable.value}
                icon="FileStandardIcon"
                isDownloadable={!!printable.url}
                isDisabled={printable.isDisabled}
                onOpen={
                  !isInWebview
                    ? () => handleOpen(printable)
                    : () => handleDownloadFile(printable)
                }
                onDownload={() => handleDownloadFile(printable)}
                data-testid={`resource-printable-${index}`}
              >
                <PreprInnerHtml text={printable.value} />
              </Resource>
            ))}
            {applets?.map((applet, index) => (
              <Resource
                icon="AppletIcon"
                key={applet}
                data-testid={`resource-applet-${index}`}
              >
                <PreprInnerHtml text={applet} />
              </Resource>
            ))}
            {videos?.map((video, index) => (
              <Resource
                icon="MediaIcon"
                key={video}
                data-testid={`resource-video-${index}`}
              >
                <PreprInnerHtml text={video} />
              </Resource>
            ))}
            {projectables?.map((projectable, index) => (
              <Resource
                icon="ProjectableIcon"
                key={projectable}
                data-testid={`resource-projectable-${index}`}
              >
                <PreprInnerHtml text={projectable} />
              </Resource>
            ))}
          </List>
        </Container>
      )}
    </Wrapper>
  );
}
